import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { AuthPageLayout } from "../components/AuthPageLayout";
import { isRefusedResetLink, useResetPasswordMutation } from "../lib/authQueries";

/** Better Auth's default password bounds, so the form refuses early. */
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 128;

export function ResetPasswordPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const resetPassword = useResetPasswordMutation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [linkRefused, setLinkRefused] = useState(false);

  const mismatch = confirm !== password;
  const showMismatch = mismatch && (submitted || confirm.length > 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!token || mismatch) return;
    try {
      await resetPassword.mutateAsync({ token, newPassword: password });
      toast.success("Password changed. Sign in with your new password.");
      await navigate({ to: "/login", replace: true });
    } catch (err) {
      // A refused link is shown here and not toasted; the query client toasts every other failure.
      if (isRefusedResetLink(err)) setLinkRefused(true);
    }
  };

  if (!token || linkRefused) {
    return (
      <AuthPageLayout title="Reset your password">
        <p className="text-sm">
          {token
            ? "This reset link has expired or was already used."
            : "This reset link is incomplete. Open the link from your email again, or request a new one."}
        </p>
        <Link
          to="/forgot-password"
          className={buttonVariants({ variant: "outline", className: "w-full" })}
        >
          Request a new link
        </Link>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout title="Choose a new password">
      <form onSubmit={submit} className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="reset-password">New password</Label>
          <Input
            id="reset-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={PASSWORD_MIN_LENGTH}
            maxLength={PASSWORD_MAX_LENGTH}
            autoComplete="new-password"
            required
          />
          <p className="text-xs text-muted-foreground">
            {PASSWORD_MIN_LENGTH} to {PASSWORD_MAX_LENGTH} characters.
          </p>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="reset-confirm">Confirm password</Label>
          <Input
            id="reset-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={PASSWORD_MIN_LENGTH}
            maxLength={PASSWORD_MAX_LENGTH}
            autoComplete="new-password"
            aria-invalid={showMismatch || undefined}
            aria-describedby={showMismatch ? "reset-confirm-error" : undefined}
            required
          />
          {showMismatch && (
            <p id="reset-confirm-error" className="text-xs text-destructive">
              The passwords don't match.
            </p>
          )}
        </div>
        <Button type="submit" className="w-full" disabled={resetPassword.isPending}>
          {resetPassword.isPending ? "Saving…" : "Set new password"}
        </Button>
      </form>
    </AuthPageLayout>
  );
}
